import { ContentFragment } from '@oneaudi/falcon-tools';
import { VendorCTAType } from '../../types';
import { SinglePriceConfigType } from '../../types/SinglePriceContent';

export interface FalconContent extends Partial<ContentFragment> {
  __type: 'aem-headless';
  fields: {
    // eslint-disable-next-line
    vendorCtaGroup?: any;
    priceConfigModel?: any;
  };
}

// we have to this due to OneGraph Migration
// because from scs to Onegraph regular has changed to "list" and retail has changed to "sale"
const normalizePriceKey = (key: string) => {
  if (key === 'regular') return 'list';
  if (key === 'retail') return 'sale';
  return key ?? '';
};

export interface AppContent {
  __type?: 'default';
  vendorCtaGroup: [
    items: {
      isVendorCtaEnabled: boolean;
      ctaVendorLink: string;
      ctaVendorTarget: '_blank' | '_self';
      ctaVendorButtonLabel: string;
      ctaVendorAdditionalClasses: string;
      ctaVendorCustomId: string;
      ctaVendorSeoAttributes: string;
      vinRecallTypeVisibility: 'pre-owned' | 'cpo';
      vinRecallLabel: string;
      ctaGubagooLabel: string;
      flowOption: 'payments' | 'buy_online' | 'trade_in';
      ctaRoadsterUrl: string;
      actionTypeRoadster: 'See payment options' | 'Start buying process';
      ctaRoadsterLabelEnglish: string;
      ctaRoadsterLabelFrench: string;
      pagePlacementTypeCarnow: 'srp' | 'vdp';
      configTypeLocationUpstart: 'srp' | 'vdp';
      configTypeLocationDarwin: 'srp' | 'vdp';
      ctaDealerId: string;
      ctaContactButtonLabel: string;
    },
  ];
  priceConfigModel: SinglePriceConfigType;
}

export const mapContent = (content: AppContent | FalconContent): AppContent => {
  if (content?.__type !== 'aem-headless') {
    return { ...content };
  }

  return normalizeContent(content);
};

const normalizeContent = (content: FalconContent): AppContent => {
  const { vendorCtaGroup, priceConfigModel } = content.fields;

  let itemsFromCustomVendorCTAs = [] as VendorCTAType[];
  let priceContentForCMS = {} as SinglePriceConfigType;
  let formattedContent = {} as any;

  if (vendorCtaGroup) {
    // eslint-disable-next-line
    const { singlePositionVendorCTAs } = vendorCtaGroup?.fields;
    // eslint-disable-next-line
    itemsFromCustomVendorCTAs = (singlePositionVendorCTAs as VendorCTAType[])?.map((item: any) => {
      const {
        isVendorCtaEnabled,
        ctaVendorLink,
        ctaVendorTarget,
        ctaVendorButtonLabel,
        ctaVendorAdditionalClasses,
        ctaVendorCustomId,
        ctaVendorSeoAttributes,
        flowOptionGubagoo,
        ctaGubagooLabel,
        ctaRoadsterUrl,
        actionTypeRoadster,
        ctaRoadsterLabelEnglish,
        ctaRoadsterLabelFrench,
        vinRecallTypeVisibility,
        vinRecallLabel,
        pagePlacementTypeCarnow,
        configTypeLocationUpstart,
        configTypeLocationDarwin,
        ctaDealerId,
        ctaContactButtonLabel,
        // eslint-disable-next-line
      } = item?.fields;

      const contentForCMS: VendorCTAType = {
        isVendorCtaEnabled,
        ctaVendorLink,
        ctaVendorTarget,
        ctaVendorButtonLabel,
        ctaVendorAdditionalClasses,
        ctaVendorCustomId,
        ctaVendorSeoAttributes,
        flowOptionGubagoo,
        ctaGubagooLabel,
        ctaRoadsterUrl,
        actionTypeRoadster,
        ctaRoadsterLabelEnglish,
        ctaRoadsterLabelFrench,
        vinRecallTypeVisibility,
        vinRecallLabel,
        pagePlacementTypeCarnow,
        configTypeLocationUpstart,
        configTypeLocationDarwin,
        ctaDealerId,
        ctaContactButtonLabel,
      };

      return contentForCMS;
    });
  }

  if (priceConfigModel) {
    const {
      mainPriceKey,
      mainPriceLabel,
      mainShortDisclaimer,
      mainLongDisclaimer,
      textOverrideMain,
      secondPriceKey,
      secondPriceLabel,
      secondShortDisclaimer,
      secondLongDisclaimer,
      textOverrideSecond,
      usedRetailLabel,
      usedRetailShortDisclaimer,
      usedRetailLongDisclaimer,
      mainPriceKeyUsed,
      textOverrideUsedMain,
      // eslint-disable-next-line
    } = priceConfigModel?.fields;

    const firstPrice = {
      priceKey: normalizePriceKey(mainPriceKey),
      label: mainPriceLabel,
      shortDisclaimer: mainShortDisclaimer,
      longDisclaimer: mainLongDisclaimer,
      textOverride: textOverrideMain,
    };

    const secondPrice = {
      priceKey: normalizePriceKey(secondPriceKey ?? ''),
      label: secondPriceLabel ?? '',
      shortDisclaimer: secondShortDisclaimer ?? '',
      longDisclaimer: secondLongDisclaimer ?? '',
      textOverride: textOverrideSecond,
    };

    const parsedUsed = {
      priceKey:
        mainPriceKeyUsed && mainPriceKeyUsed?.length ? normalizePriceKey(mainPriceKeyUsed) : 'sale',
      label: usedRetailLabel ?? '',
      shortDisclaimer: usedRetailShortDisclaimer ?? '',
      longDisclaimer: usedRetailLongDisclaimer ?? '',
      textOverride: textOverrideUsedMain,
    };

    const isSinglePrice = !secondPriceKey || secondPriceKey === 'null' || !secondPriceKey?.length;

    const parsedNew = isSinglePrice ? [firstPrice] : [firstPrice, secondPrice];

    priceContentForCMS = {
      newVehiclePrice: parsedNew,
      usedVehiclePrice: parsedUsed,
    };
  }

  formattedContent = {
    vendorCtaGroup: itemsFromCustomVendorCTAs,
    priceConfigModel: priceContentForCMS,
  };

  return formattedContent;
};

export default mapContent;
